// =====================================================================
// Faz AB (2026-05-07) — Product 360 View
// =====================================================================
// URL: /product360/:id veya arama ile ürün seçilince — tek ürünün her şeyi.
//
// Customer 360'ın ürün versiyonu. Owner bir ürünü düşünürken:
//   - Ne kadar satıyor?
//   - Kim alıyor?
//   - Hangi ürünle birlikte alınıyor?
//   - Sepete kim ekleyip almadı (talep sinyali)?
//   - NPS yorumlarda bu ürün geçti mi?
//
// Endpoint: GET /api/admin/insights/product/:id
//
// Sayfa header'da arama kutusu — admin ürün adı veya SKU yazıp seçebilir.
// =====================================================================
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Card, Typography, Grid, Chip, Table, TableHead, TableRow, TableCell,
  TableBody, CircularProgress, Alert, IconButton, Tooltip, Avatar,
  Autocomplete, TextField
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import InventoryIcon from "@mui/icons-material/Inventory";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PeopleIcon from "@mui/icons-material/People";
import API from "../services/api";

const fmtMoney = (v) => Number(v || 0).toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " ₺";
const fmtNum = (v) => Number(v || 0).toLocaleString("tr-TR");
const fmtDate = (s) => s ? new Date(s).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" }) : "—";

export default function Product360() {
  const { id: paramId } = useParams();
  const navigate = useNavigate();

  // State'ler
  const [productId, setProductId] = useState(paramId ? parseInt(paramId, 10) : null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Ürün arama (Autocomplete) için
  const [allProducts, setAllProducts] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Tüm ürünleri arama için bir kez yükle (autocomplete için)
  // Çok ürün varsa bu büyük olabilir; ama admin'iz N=350'lerde — ok
  useEffect(() => {
    setSearchLoading(true);
    API.get("/api/admin/products?limit=500")
      .then(r => {
        const items = r.data?.items || r.data || [];
        setAllProducts(items.map(p => ({
          id: p.id,
          label: `${p.name}${p.sku ? ` [${p.sku}]` : ""}`,
          name: p.name,
          sku: p.sku
        })));
      })
      .catch(() => {})
      .finally(() => setSearchLoading(false));
  }, []);

  // productId değişince detay çek
  useEffect(() => {
    if (!productId) { setData(null); return; }
    setLoading(true);
    API.get(`/api/admin/insights/product/${productId}`)
      .then(r => { setData(r.data); setErr(null); })
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [productId]);

  const handleSelectProduct = (newValue) => {
    if (newValue?.id) {
      setProductId(newValue.id);
      navigate(`/product360/${newValue.id}`);
    }
  };

  // Daily trend için max revenue (chart bar yüksekliği)
  const trendMax = data?.trend
    ? Math.max(...data.trend.map(t => Number(t.revenue) || 0), 1)
    : 1;

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {/* Üst başlık + arama */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Product 360</Typography>
          <Typography variant="body2" color="text.secondary">
            Tek ürünün tüm verisi tek sayfada
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 320 }}>
          <Autocomplete
            options={allProducts}
            value={allProducts.find(p => p.id === productId) || null}
            onChange={(_, v) => handleSelectProduct(v)}
            loading={searchLoading}
            sx={{ flex: 1 }}
            renderInput={(params) => (
              <TextField {...params} size="small" placeholder="Ürün ara (ad/SKU)..." />
            )}
            getOptionLabel={(opt) => opt?.label || ""}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
          />
          {productId && (
            <Tooltip title="Yenile">
              <IconButton onClick={() => productId && setProductId(productId + 0)}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {!productId && (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Üst kutudan ürün seçin — o ürünün tüm verisi (satış, müşteri, sepet, cross-sell, NPS) tek sayfada görünür.
        </Alert>
      )}

      {loading && <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>}
      {err && <Alert severity="error" sx={{ mt: 2 }}>{err}</Alert>}

      {data && data.product && (
        <>
          {/* Ürün master kartı */}
          <Card sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
            <Box sx={{ display: "flex", gap: 2.5, alignItems: "flex-start", flexWrap: "wrap" }}>
              <Avatar
                src={data.product.image_url} variant="rounded"
                sx={{ width: 100, height: 100, bgcolor: "#f1f5f9", fontSize: 40 }}
              >📦</Avatar>
              <Box sx={{ flex: 1, minWidth: 250 }}>
                <Typography variant="h5" fontWeight={800}>{data.product.name}</Typography>
                <Box sx={{ display: "flex", gap: 1, my: 1, flexWrap: "wrap" }}>
                  {data.product.sku && <Chip label={`SKU: ${data.product.sku}`} size="small" variant="outlined" />}
                  {data.product.brand && <Chip label={`Marka: ${data.product.brand}`} size="small" sx={{ bgcolor: "#fef3c7" }} />}
                  {data.product.category_name && <Chip label={data.product.category_name} size="small" sx={{ bgcolor: "#e3f2fd" }} />}
                  <Chip
                    label={data.product.is_active ? "Aktif" : "Pasif"}
                    size="small"
                    sx={{ bgcolor: data.product.is_active ? "#10b981" : "#94a3b8", color: "white" }}
                  />
                </Box>
                <Typography variant="caption" color="text.disabled">
                  Tedarikçi: {data.product.supplier_name || "—"}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="h4" fontWeight={800} color="#1565C0">
                  {fmtMoney(data.product.price)}
                </Typography>
                <Typography variant="caption" color="text.disabled">satış fiyatı</Typography>
              </Box>
            </Box>
          </Card>

          {/* 4 KPI: stok / 30g ciro / 90g ciro / 365g ciro */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} md={3}>
              <Card sx={{ p: 2, borderRadius: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                  <InventoryIcon sx={{ color: "#1565C0", fontSize: 18 }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>STOK</Typography>
                </Box>
                <Typography variant="h4" fontWeight={800}>{fmtNum(data.product.stock)}</Typography>
                {data.product.daily_velocity > 0 && data.product.days_until_stockout != null && (
                  <Typography variant="caption" sx={{
                    color: data.product.days_until_stockout <= 7 ? "#ef4444" :
                           data.product.days_until_stockout <= 14 ? "#f59e0b" : "#10b981",
                    fontWeight: 700
                  }}>
                    {Math.round(data.product.days_until_stockout)} gün kaldı
                  </Typography>
                )}
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ p: 2, borderRadius: 3 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>30 GÜN CİRO</Typography>
                <Typography variant="h4" fontWeight={800} color="#10b981">{fmtMoney(data.sales?.rev_30d)}</Typography>
                <Typography variant="caption" color="text.disabled">{fmtNum(data.sales?.qty_30d)} adet</Typography>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ p: 2, borderRadius: 3 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>90 GÜN CİRO</Typography>
                <Typography variant="h4" fontWeight={800} color="#3b82f6">{fmtMoney(data.sales?.rev_90d)}</Typography>
                <Typography variant="caption" color="text.disabled">
                  {fmtNum(data.sales?.orders_90d)} sipariş · {fmtNum(data.sales?.unique_buyers_90d)} müşteri
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card sx={{ p: 2, borderRadius: 3 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>1 YIL CİRO</Typography>
                <Typography variant="h4" fontWeight={800} color="#8b5cf6">{fmtMoney(data.sales?.rev_365d)}</Typography>
                <Typography variant="caption" color="text.disabled">{fmtNum(data.sales?.qty_365d)} adet</Typography>
              </Card>
            </Grid>
          </Grid>

          {/* 30 gün trend mini-chart */}
          {data.trend && data.trend.length > 0 && (
            <Card sx={{ p: 2.5, borderRadius: 3, mb: 3 }}>
              <Typography variant="h6" fontWeight={700} mb={2}>
                <TrendingUpIcon sx={{ verticalAlign: "middle", mr: 1, color: "#1565C0" }} />
                30 Gün Satış Trendi
              </Typography>
              <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.5, height: 100 }}>
                {data.trend.map((t, i) => {
                  const heightPct = (Number(t.revenue) / trendMax) * 100;
                  return (
                    <Tooltip key={i} title={`${fmtDate(t.day)}: ${fmtMoney(t.revenue)} (${t.qty} adet)`} placement="top">
                      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                        <Box sx={{
                          width: "100%", height: `${Math.max(heightPct, 2)}%`,
                          bgcolor: "#1565C0", borderRadius: "3px 3px 0 0",
                          transition: "height 0.3s",
                          "&:hover": { bgcolor: "#0D47A1" }
                        }} />
                        <Typography variant="caption" sx={{ fontSize: 9, color: "text.disabled" }}>
                          {fmtDate(t.day)}
                        </Typography>
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Card>
          )}

          <Grid container spacing={2}>
            {/* Top buyers */}
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
                <Box sx={{ p: 2, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 1 }}>
                  <PeopleIcon sx={{ color: "#10b981" }} />
                  <Typography variant="h6" fontWeight={700}>En Çok Alan Müşteriler</Typography>
                </Box>
                {data.top_buyers.length === 0 ? (
                  <Box sx={{ p: 3 }}><Typography color="text.secondary" fontSize={13}>Henüz alan yok</Typography></Box>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Müşteri</strong></TableCell>
                        <TableCell align="right"><strong>Adet</strong></TableCell>
                        <TableCell align="right"><strong>Ciro</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.top_buyers.map((b, i) => (
                        <TableRow key={b.id} hover sx={{ cursor: "pointer" }} onClick={() => navigate(`/customer/${b.id}`)}>
                          <TableCell>
                            <Typography fontSize={13} fontWeight={i < 3 ? 700 : 500}>
                              {i < 3 && <span style={{ marginRight: 4 }}>{i === 0 ? "🥇" : i === 1 ? "🥈" : "🥉"}</span>}
                              {b.company_name || b.name}
                            </Typography>
                            <Typography variant="caption" color="text.disabled" fontSize={10}>
                              {b.order_count} sipariş · son: {fmtDate(b.last_order_at)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{fmtNum(b.total_qty)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: "#10b981" }}>
                            {fmtMoney(b.total_revenue)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>
            </Grid>

            {/* Cross-sell */}
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
                <Box sx={{ p: 2, bgcolor: "#fff7ed", borderBottom: "1px solid #fed7aa", display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h6" fontWeight={700}>🔗 Birlikte Alınan Ürünler</Typography>
                </Box>
                {data.cross_sell.length === 0 ? (
                  <Box sx={{ p: 3 }}><Typography color="text.secondary" fontSize={13}>Cross-sell pair yok</Typography></Box>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Eş Ürün</strong></TableCell>
                        <TableCell align="center"><strong>Birlikte</strong></TableCell>
                        <TableCell align="center"><strong>Confidence</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.cross_sell.map((c, i) => (
                        <TableRow key={i} hover sx={{ cursor: "pointer" }} onClick={() => setProductId(c.paired_product_id)}>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Avatar src={c.image_url} variant="rounded" sx={{ width: 32, height: 32 }}>📦</Avatar>
                              <Typography fontSize={13} fontWeight={600}>{c.name}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 700 }}>{c.co_occurrence_count}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={`%${Math.round(c.confidence * 100)}`}
                              size="small"
                              sx={{ bgcolor: "#f97316", color: "white", fontWeight: 700, fontSize: 11 }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>
            </Grid>

            {/* Sepete koyup almayan */}
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
                <Box sx={{ p: 2, bgcolor: "#eff6ff", borderBottom: "1px solid #dbeafe" }}>
                  <Typography variant="h6" fontWeight={700}>🛒 Sepete Koyup Almayan</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Talep var ama sipariş yok — kişisel hatırlatma fırsatı
                  </Typography>
                </Box>
                {data.cart_no_order.length === 0 ? (
                  <Box sx={{ p: 3 }}><Typography color="text.secondary" fontSize={13}>Bu ürün için yok</Typography></Box>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Müşteri</strong></TableCell>
                        <TableCell align="right"><strong>Adet</strong></TableCell>
                        <TableCell><strong>Eklenme</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.cart_no_order.map((c, i) => (
                        <TableRow key={i} hover sx={{ cursor: "pointer" }} onClick={() => navigate(`/customer/${c.id}`)}>
                          <TableCell>
                            <Typography fontSize={13} fontWeight={600}>{c.company_name || c.name}</Typography>
                            <Typography variant="caption" color="text.disabled" fontSize={10}>{c.email}</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{c.quantity}</TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{fmtDate(c.added_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>
            </Grid>

            {/* NPS yorumlarda */}
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, overflow: "hidden" }}>
                <Box sx={{ p: 2, bgcolor: "#fef2f2", borderBottom: "1px solid #fee2e2" }}>
                  <Typography variant="h6" fontWeight={700}>⭐ NPS Yorumlarda</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Bu ürünün adını içeren NPS yanıtları
                  </Typography>
                </Box>
                {data.nps_mentions.length === 0 ? (
                  <Box sx={{ p: 3 }}><Typography color="text.secondary" fontSize={13}>Yorumda geçmiyor</Typography></Box>
                ) : (
                  <Box sx={{ p: 2 }}>
                    {data.nps_mentions.map((m, i) => (
                      <Card variant="outlined" key={i} sx={{ p: 1.5, mb: 1 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                          <Chip
                            label={`${m.score}/10`}
                            size="small"
                            sx={{
                              bgcolor: m.category === "promoter" ? "#10b981" :
                                       m.category === "passive" ? "#f59e0b" : "#ef4444",
                              color: "white", fontWeight: 700
                            }}
                          />
                          <Typography variant="caption" color="text.disabled">
                            {m.company_name || m.user_name}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontSize: 13 }}>{m.feedback}</Typography>
                      </Card>
                    ))}
                  </Box>
                )}
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}
